import { Injectable } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from "@angular/forms";

import {
  UrlValidator,
  ColorValidator,
  LocaleDataService,
  MainConfig,
} from "@atenea-cms";
import { Product } from "./product.model";

import { Slide, SlideAction } from "../../common/models";

import {
  Award,
  Download,
  Filter,
  Header,
  Highlight,
  Images,
  ImageEffect,
  ImageWithEffects,
  ImageWithHotPoints,
  HotPoint,
  // PhotoForList,
  GalleryItem,
  Spec,
  SubSpec,
  Video,
  Videos,
  Software,
  SoftwareSlide,
  WhereToBuy,
} from "./models";
import { RequiredValidator } from "@angular/forms/src/directives/validators";
import { has } from "lodash";

@Injectable()
export class ProductFormService {
  slideTextBackgroundDefault: string;

  constructor(
    private fb: FormBuilder,
    private localeService: LocaleDataService,
    private mainconfig: MainConfig
  ) {
    if (has(mainconfig.modules, "common.slide.textBackground.default")) {
      this.slideTextBackgroundDefault =
        mainconfig.modules.common.slide.textBackground.default;
    }
  }

  getForm(product: Product = new Product()): FormGroup {
    return this.fb.group({
      _id: [product._id],
      name: [product.name, Validators.required],
      slug: [product.slug, Validators.required],
      sku: [product.sku, Validators.required],
      mpn: [product.mpn],
      ean: [product.ean],
      slogan: [product.slogan],
      description: [product.description],
      metaDescription: [product.metaDescription],
      prominentFeatures: [product.prominentFeatures],
      parent: [product.parent ? product.parent._id : null],
      category: [product.category ? product.category._id : null],
      completedLocales: [product.completedLocales],
      serie: [product.serie ? product.serie._id : null],
      prominent: [product.prominent],
      modelType: [product.modelType],
      modelPrimaryColor: [product.modelPrimaryColor],
      modelSecondaryColor: [product.modelSecondaryColor],
      modelOptionName: [product.modelOptionName],
      modelOptionIcon: [product.modelOptionIcon],
      position: [product.position],
      active: [product.active],
      discontinued: [product.discontinued],
      preview: [product.preview],
      published: [product.published],
      images: this.getImagesForm(product.images),
      header: this.getHeaderForm(product.header),
      imageWithHotPoints: this.getImageWithHotPointsForm(
        product.imageWithHotPoints
      ),
      imageWithEffects: this.getImageWithEffectsForm(product.imageWithEffects),
      suggestedInCategories: this.fb.array(
        product.suggestedInCategories.map(
          (item) => new FormControl(item instanceof Object ? item._id : item)
        )
      ),
      awards: this.fb.array(
        product.awards.map((item) => this.getAwardForm(item))
      ),
      whereToBuy: this.fb.array(
        product.whereToBuy.map((item) => this.getWhereToBuyForm(item))
      ),
      downloads: this.fb.array(
        product.downloads.map((item) => this.getDownloadForm(item))
      ),
      filters: this.fb.array(
        product.filters.map((item) => this.getFilterForm(item))
      ),
      highlights: this.fb.array(
        product.highlights.map((item) => this.getHighlightForm(item))
      ),
      // photosForList: this.fb.array(product.photosForList.map(item => this.getPhotoForListForm(item))),
      gallery: this.fb.array(
        product.gallery.map((item) => this.getGalleryItemForm(item))
      ),
      slides: this.fb.array(
        product.slides.map((item) => this.getSlideForm(item))
      ),
      specs: this.fb.array(product.specs.map((item) => this.getSpecForm(item))),
      videos: this.getVideosForm(product.videos),
      software: this.getSoftwareForm(product.software),
    });
  }

  getImagesForm(images: Images = new Images()): FormGroup {
    return this.fb.group({
      main: [images.main],
      list: [images.list],
      logo: [images.logo],
      logoDark: [images.logoDark],
      header: [images.header],
      specs: [images.specs],
    });
  }

  getHeaderForm(header: Header = new Header()): FormGroup {
    return this.fb.group({
      image: [header.image],
      video: [header.video],
      type: [header.type],
    });
  }

  getSoftwareForm(software: Software = new Software()): FormGroup {
    return this.fb.group({
      title: [software.title],
      slides: this.fb.array(
        software.slides.map((slide) => this.getSoftwareSlideForm(slide))
      ),
    });
  }

  getSoftwareSlideForm(
    softwareSlide: SoftwareSlide = new SoftwareSlide()
  ): FormGroup {
    return this.fb.group({
      _id: [softwareSlide._id],
      title: [softwareSlide.title],
      content: [softwareSlide.content],
      image: [softwareSlide.image],
      position: [softwareSlide.position],
    });
  }

  getImageWithHotPointsForm(
    imageWithHotPoints: ImageWithHotPoints = new ImageWithHotPoints()
  ): FormGroup {
    return this.fb.group({
      title: [imageWithHotPoints.title],
      description: [imageWithHotPoints.description],
      image: [imageWithHotPoints.image],
      points: this.fb.array(
        imageWithHotPoints.points.map((point) => this.getHotPointForm(point))
      ),
    });
  }

  getHotPointForm(hotPoint: HotPoint = new HotPoint()): FormGroup {
    return this.fb.group({
      _id: [hotPoint._id],
      image: [hotPoint.image],
      content: [hotPoint.content],
      x: [hotPoint.x],
      y: [hotPoint.y],
    });
  }

  getVideosForm(videos: Videos = new Videos()): FormGroup {
    return this.fb.group({
      name: [videos.name],
      textColor: [videos.textColor],
      background: [videos.background],
      backgroundMobile: [videos.backgroundMobile],
      backgroundVideo: [videos.backgroundVideo],
      backgroundFixed: [videos.backgroundFixed],
      videos: this.fb.array(
        videos.videos.map((video) => this.getVideoForm(video))
      ),
    });
  }

  getVideoForm(video: Video = new Video()): FormGroup {
    return this.fb.group({
      _id: [video._id],
      youtubeCode: [video.youtubeCode],
      poster: [video.poster],
      position: [video.position],
      title: [video.title],
    });
  }

  getImageWithEffectsForm(
    imageWithEffects: ImageWithEffects = new ImageWithEffects()
  ): FormGroup {
    return this.fb.group({
      image: [imageWithEffects.image],
      effects: this.fb.array(
        imageWithEffects.effects.map((effect) =>
          this.getImageEffectForm(effect)
        )
      ),
    });
  }

  getImageEffectForm(imageEffect: ImageEffect = new ImageEffect()): FormGroup {
    return this.fb.group({
      name: [imageEffect.name],
      image: [imageEffect.image],
    });
  }

  getAwardForm = (award: Award = new Award()): FormGroup => {
    if (!award._id && !award.locale) {
      award.locale = this.localeService.getCurrentLocale().code;
    }

    return this.fb.group({
      _id: [award._id],
      award: [
        award.award
          ? typeof award.award === "object"
            ? award.award._id
            : award.award
          : null,
        [Validators.required],
      ],
      url: [award.url, [Validators.required, UrlValidator]],
      review: [award.review],
      position: [award.position],
      locale: [award.locale],
    });
  };

  getWhereToBuyForm = (
    whereToBuy: WhereToBuy = new WhereToBuy()
  ): FormGroup => {
    return this.fb.group({
      _id: [whereToBuy._id],
      store: [
        whereToBuy.store
          ? typeof whereToBuy.store === "object"
            ? whereToBuy.store._id
            : whereToBuy.store
          : null,
        [Validators.required],
      ],
      url: [whereToBuy.url, [Validators.required, UrlValidator]],
    });
  };

  getDownloadForm = (download: Download = new Download()): FormGroup => {
    if (!download._id) {
      download.locales = this.localeService
        .getLocalesAvailable()
        .map((locale) => locale.code);
    }

    return this.fb.group({
      _id: [download._id],
      name: [download.name],
      file: [download.file],
      type: [download.type ? download.type._id : null],
      position: [download.position],
      locales: [download.locales],
    });
  };

  getFilterForm(filter: Filter = new Filter()): FormGroup {
    return this.fb.group({
      _id: [filter._id],
      filter: [
        filter.filter instanceof Object ? filter.filter._id : filter.filter,
      ],
      value: [filter.value],
    });
  }

  getHighlightForm(highlight: Highlight = new Highlight()): FormGroup {
    return this.fb.group({
      _id: [highlight._id],
      name: [highlight.name, Validators.required],
      icon: [highlight.icon],
      position: [highlight.position],
    });
  }

  // getPhotoForListForm(photoForList: PhotoForList = new PhotoForList()): FormGroup {
  //   return this.fb.group({
  //     _id: [photoForList._id],
  //     image: [photoForList.image],
  //     alt: [photoForList.alt],
  //     position: [photoForList.position]
  //   });
  // }

  getGalleryItemForm(item: GalleryItem = new GalleryItem()): FormGroup {
    return this.fb.group({
      _id: [item._id],
      image: [item.image],
      video: [item.video],
      thumbnail: [item.thumbnail],
      backgroundImage: [item.backgroundImage],
      backgroundColor: [item.backgroundColor, ColorValidator],
      alt: [item.alt],
      position: [item.position],
      showInList: [item.showInList],
    });
  }

  getSlideForm = (
    slide: Slide = new Slide(this.slideTextBackgroundDefault)
  ): FormGroup => {
    return this.fb.group({
      _id: [slide._id],
      image: [slide.image],
      video: [slide.video],
      mediaPosition: [slide.mediaPosition],
      title: [slide.title],
      content: [slide.content],
      secondaryContent: [slide.secondaryContent],
      contentPosition: [slide.contentPosition],
      backgroundImage: [slide.backgroundImage],
      backgroundVideo: [slide.backgroundVideo],
      backgroundPosition: [slide.backgroundPosition],
      backgroundColor: [slide.backgroundColor],
      backgroundColorSecondary: [slide.backgroundColorSecondary],
      backgroundColorAlias: [slide.backgroundColorAlias, Validators.required],
      backgroundFixed: [slide.backgroundFixed],
      backgroundPatternAligment: [slide.backgroundPatternAligment],
      backgroundPatternStyle: [slide.backgroundPatternStyle],
      height: [slide.height],
      textColor: [slide.textColor],
      textBackground: [slide.textBackground],
      actions: this.fb.array(
        slide.actions.map((item) => this.getSlideActionForm(item))
      ),
      actionsAlign: [slide.actionsAlign],
      position: [slide.position],
      active: [slide.active],
      vintageMode: [slide.vintageMode],
    });
  };

  getSlideActionForm(slideAction: SlideAction = new SlideAction()) {
    return this.fb.group({
      _id: [slideAction._id],
      icon: [slideAction.icon],
      label: [slideAction.label],
      type: [slideAction.type, Validators.required],
      image: [slideAction.image],
      video: [slideAction.video],
      backgroundImage: [slideAction.backgroundImage],
      backgroundVideo: [slideAction.backgroundVideo],
      colorPrimary: [slideAction.colorPrimary],
      colorSecondary: [slideAction.colorSecondary],
      url: [slideAction.url],
      youtubeCode: [slideAction.youtubeCode],
      poster: [slideAction.youtubePoster],
      anchor: [slideAction.anchor],
      anchorSection: [slideAction.anchorSection],
      position: [slideAction.position],
      style: [slideAction.style],
    });
  }

  getSpecForm(spec: Spec = new Spec()): FormGroup {
    return this.fb.group({
      _id: [spec._id],
      name: [spec.name],
      value: [spec.value],
      subSpecs: this.fb.array(
        spec.subSpecs.map((item) => this.getSubSpecForm(item))
      ),
      position: [spec.position],
    });
  }

  getSubSpecForm(subSpec: SubSpec = new SubSpec()): FormGroup {
    return this.fb.group({
      _id: [subSpec._id],
      name: [subSpec.name],
      value: [subSpec.value],
      position: [subSpec.position],
    });
  }
}



// WEBPACK FOOTER //
// ./libs/atenea-cms-brand/src/products/shared/product-form.service.ts